import React, { useEffect, useRef, useState, useCallback, useContext } from "react";
import { Box, Typography, IconButton, Avatar, Slide } from "@material-ui/core";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import { toast } from "react-toastify";

import api from "../../services/api";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";

// Ligação pela API oficial do WhatsApp.
//
// O áudio NÃO passa pelo nosso servidor: o navegador fala direto com o
// WhatsApp do cliente por WebRTC. O backend só carrega as descrições de
// sessão (SDP) de um lado para o outro. Por isso tudo aqui gira em torno de
// um único RTCPeerConnection.
//
// A oferta de sessão vive poucos segundos — se demorarmos para responder, a
// Meta desiste da ligação. Nada de esperar rede ou banco no meio do caminho.

const SERVIDORES_ICE = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" }
];

const formatarDuracao = (segundos) => {
  const m = Math.floor(segundos / 60).toString().padStart(2, "0");
  const s = Math.floor(segundos % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const LigacaoOficial = ({ chamadaParaIniciar, aoEncerrar }) => {
  const { user } = useContext(AuthContext);

  const [chamada, setChamada] = useState(null); // {callId, numero, nome, whatsappId, estado}
  const [mudo, setMudo] = useState(false);
  const [segundos, setSegundos] = useState(0);

  const pcRef = useRef(null);
  const streamLocalRef = useRef(null);
  const audioRef = useRef(null);
  const chamadaRef = useRef(null);

  useEffect(() => {
    chamadaRef.current = chamada;
  }, [chamada]);

  const limpar = useCallback(() => {
    try {
      if (pcRef.current) {
        pcRef.current.getSenders().forEach((s) => s.track && s.track.stop());
        pcRef.current.close();
      }
    } catch (e) {}
    pcRef.current = null;

    try {
      if (streamLocalRef.current) {
        streamLocalRef.current.getTracks().forEach((t) => t.stop());
      }
    } catch (e) {}
    streamLocalRef.current = null;

    setChamada(null);
    setMudo(false);
    setSegundos(0);
    if (aoEncerrar) aoEncerrar();
  }, [aoEncerrar]);

  // Monta a conexão e já pede o microfone. Pedir cedo evita a permissão do
  // navegador aparecer no meio do toque e a ligação cair.
  const prepararConexao = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamLocalRef.current = stream;

    const pc = new RTCPeerConnection({ iceServers: SERVIDORES_ICE });
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));

    pc.ontrack = (ev) => {
      if (audioRef.current && ev.streams && ev.streams[0]) {
        audioRef.current.srcObject = ev.streams[0];
        audioRef.current.play().catch(() => {});
      }
    };

    pcRef.current = pc;
    return pc;
  }, []);

  // A Meta espera o SDP completo, com os candidatos ICE dentro. Mandar antes
  // da coleta terminar resulta em ligação que conecta e fica muda.
  const esperarIce = (pc) =>
    new Promise((resolve) => {
      if (pc.iceGatheringState === "complete") return resolve();
      const checar = () => {
        if (pc.iceGatheringState === "complete") {
          pc.removeEventListener("icegatheringstatechange", checar);
          resolve();
        }
      };
      pc.addEventListener("icegatheringstatechange", checar);
      setTimeout(resolve, 3000); // não trava a ligação se o ICE demorar
    });

  const atender = useCallback(async () => {
    const atual = chamadaRef.current;
    if (!atual?.ofertaSdp) return;

    try {
      const pc = await prepararConexao();
      await pc.setRemoteDescription({ type: "offer", sdp: atual.ofertaSdp });
      const resposta = await pc.createAnswer();
      await pc.setLocalDescription(resposta);
      await esperarIce(pc);

      await api.post(`/whatsapp-official/${atual.whatsappId}/chamada/atender`, {
        callId: atual.callId,
        sdp: pc.localDescription.sdp
      });

      setChamada((c) => (c ? { ...c, estado: "em_curso" } : c));
    } catch (e) {
      toast.error(
        e?.response?.data?.error ||
          "Não foi possível atender. Verifique a permissão do microfone."
      );
      limpar();
    }
  }, [prepararConexao, limpar]);

  const recusar = useCallback(async () => {
    const atual = chamadaRef.current;
    if (!atual) return limpar();
    try {
      await api.post(`/whatsapp-official/${atual.whatsappId}/chamada/recusar`, {
        callId: atual.callId
      });
    } catch (e) {}
    limpar();
  }, [limpar]);

  const desligar = useCallback(async () => {
    const atual = chamadaRef.current;
    if (!atual) return limpar();
    try {
      await api.post(`/whatsapp-official/${atual.whatsappId}/chamada/encerrar`, {
        callId: atual.callId
      });
    } catch (e) {}
    limpar();
  }, [limpar]);

  // Ligar para o cliente
  useEffect(() => {
    if (!chamadaParaIniciar) return;
    let cancelado = false;

    (async () => {
      try {
        const pc = await prepararConexao();
        const oferta = await pc.createOffer({ offerToReceiveAudio: true });
        await pc.setLocalDescription(oferta);
        await esperarIce(pc);
        if (cancelado) return;

        const { data } = await api.post(
          `/whatsapp-official/${chamadaParaIniciar.whatsappId}/chamada/ligar`,
          {
            numero: chamadaParaIniciar.numero,
            sdp: pc.localDescription.sdp
          }
        );

        console.log("[Ligacao] resposta ao iniciar:", data);
        const callId =
          data?.calls?.[0]?.id || data?.id || data?.call_id || "";

        setChamada({
          callId,
          numero: chamadaParaIniciar.numero,
          nome: chamadaParaIniciar.nome,
          whatsappId: chamadaParaIniciar.whatsappId,
          estado: "chamando"
        });
      } catch (e) {
        toast.error(
          e?.response?.data?.error ||
            "Não foi possível iniciar a ligação. Verifique a permissão do microfone."
        );
        limpar();
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [chamadaParaIniciar, prepararConexao, limpar]);

  // Eventos vindos da Meta
  useEffect(() => {
    if (!user?.companyId) return;
    const socket = socketConnection({ companyId: user.companyId });

    const aoReceber = async (dados) => {
      const evento = String(dados?.evento || "").toLowerCase();
      const sessao = dados?.sessao;
      const atual = chamadaRef.current;

      // Encerramento vale para qualquer estado.
      if (evento.includes("terminate") || evento.includes("reject")) {
        if (atual && atual.callId === dados.callId) limpar();
        return;
      }

      // Resposta de sessão da ligação que NÓS iniciamos.
      const aguardandoResposta =
        atual &&
        sessao?.sdp &&
        sessao?.sdp_type === "answer" &&
        pcRef.current &&
        !pcRef.current.currentRemoteDescription;

      if (aguardandoResposta) {
        try {
          await pcRef.current.setRemoteDescription({
            type: "answer",
            sdp: sessao.sdp
          });
          // Guarda o identificador que a Meta usa de verdade, para conseguir
          // desligar depois.
          setChamada((c) =>
            c ? { ...c, estado: "em_curso", callId: dados.callId || c.callId } : c
          );
        } catch (e) {
          console.error("[Ligacao] falha ao aplicar a sessão:", e);
          toast.error("A ligação conectou mas o áudio falhou.");
        }
        return;
      }

      // Chamada nova do cliente.
      if (!atual && sessao?.sdp && sessao?.sdp_type === "offer") {
        setChamada({
          callId: dados.callId,
          numero: dados.numero,
          nome: dados.numero,
          whatsappId: dados.whatsappId,
          ofertaSdp: sessao.sdp,
          estado: "tocando"
        });
      }
    };

    socket.on(`company-${user.companyId}-officialCall`, aoReceber);
    return () => {
      socket.off(`company-${user.companyId}-officialCall`, aoReceber);
    };
  }, [user?.companyId, limpar]);

  // Cronômetro
  useEffect(() => {
    if (chamada?.estado !== "em_curso") return;
    const t = setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [chamada?.estado]);

  const alternarMudo = () => {
    const stream = streamLocalRef.current;
    if (!stream) return;
    const novo = !mudo;
    stream.getAudioTracks().forEach((t) => (t.enabled = !novo));
    setMudo(novo);
  };

  if (!chamada) return <audio ref={audioRef} autoPlay />;

  const tocando = chamada.estado === "tocando";
  const chamando = chamada.estado === "chamando";

  return (
    <>
      <audio ref={audioRef} autoPlay />
      <Slide direction="down" in mountOnEnter unmountOnExit>
        <Box
          style={{
            position: "fixed",
            top: 16,
            right: 16,
            zIndex: 4000,
            background: "#111b21",
            color: "#fff",
            borderRadius: 14,
            padding: "16px 20px",
            minWidth: 300,
            boxShadow: "0 10px 30px rgba(0,0,0,.35)"
          }}
        >
          <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Avatar style={{ background: "#00a884" }}>
              <CallIcon />
            </Avatar>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Typography style={{ fontWeight: 600, fontSize: 15 }} noWrap>
                {chamada.nome || chamada.numero}
              </Typography>
              <Typography style={{ fontSize: 12.5, opacity: 0.75 }}>
                {tocando
                  ? "Chamada recebida"
                  : chamando
                  ? "Chamando..."
                  : formatarDuracao(segundos)}
              </Typography>
            </Box>
          </Box>

          <Box style={{ display: "flex", gap: 10, marginTop: 14, justifyContent: "flex-end" }}>
            {tocando && (
              <IconButton
                onClick={atender}
                style={{ background: "#00a884", color: "#fff" }}
                title="Atender"
              >
                <CallIcon />
              </IconButton>
            )}
            {!tocando && (
              <IconButton
                onClick={alternarMudo}
                style={{ background: "#2a3942", color: "#fff" }}
                title={mudo ? "Ativar microfone" : "Silenciar"}
              >
                {mudo ? <MicOffIcon /> : <MicIcon />}
              </IconButton>
            )}
            <IconButton
              onClick={tocando ? recusar : desligar}
              style={{ background: "#e53935", color: "#fff" }}
              title={tocando ? "Recusar" : "Desligar"}
            >
              <CallEndIcon />
            </IconButton>
          </Box>
        </Box>
      </Slide>
    </>
  );
};

export default LigacaoOficial;
